// i18next-extract-mark-ns-start protection-policy

import {AnchorLink} from 'components/AnchorLink';
import {Article} from 'components/Article';
import {InternalPageLink} from 'components/links/Pages';
import {SEO} from 'components/SEO';
import {graphql} from 'gatsby';
import {Trans, useI18next} from 'gatsby-plugin-react-i18next';
import React from 'react';

const Page: React.FC = () => {
  const {t} = useI18next();
  return (
    <Article wide>
      <SEO
        path="protection-policy"
        title={t('Protection Policy')}
        description={t('Customer data protection policy (businesses and end users).')}
      />
      <Trans parent="h1">Customer data protection policy</Trans>
      <div>
        <Trans parent="h2">Data controller</Trans>
      </div>
      <Trans parent="p">
        The controller of your personal data is MONEI DIGITAL PAYMENTS S.L. (hereinafter, "MONEI"),
        with registered office at Calle Palestina, 1, Entreplanta, 29007, Málaga.
      </Trans>
      <div>
        <Trans parent="h2">Data Protection Officer</Trans>
      </div>
      <Trans parent="p">
        MONEI has appointed a Data Protection Officer in charge of supervising compliance with data
        protection matters, whose contact details are:{' '}
        <AnchorLink href="mailto:support@monei.com">support@monei.com</AnchorLink> and whom you can
        contact if you have any questions or complaints on the matter.
      </Trans>
      <div>
        <Trans parent="h2">Processing of personal data</Trans>
      </div>
      <div>
        <Trans parent="h3">Data types</Trans>
      </div>
      <ul>
        <Trans parent="li">
          In the case of a company (Business): Country, Legal form, Tax identification number (NIF)
          (there is direct integration with the commercial register, which checks it automatically),
          Business name, Business category, Business description, Website, Company address, Business
          email, Company phone number, Corporate bank account information (the client can connect
          their corporate bank account via Open Banking or attach the bank ownership certificate for
          a corporate IBAN).
        </Trans>
        <Trans parent="li">
          Legal representative (or agent) details: Name and surname, identification document (DNI,
          NIE, Passport) and video selfie, including biometric data, Country of tax residence,
          Personal address, Email and telephone number, digital signature.
        </Trans>
        <Trans parent="li">
          For self-employed workers: Country, Tax Identification Number (NIF), Census status
          certificate details (direct integration is available), Legal business name, Business
          category, Business description, Website, Company address, Business email, Company phone
          number, Corporate bank account information (the client can connect their corporate bank
          account via Open Banking or attach the bank ownership certificate for a corporate IBAN),
          digital signature.
        </Trans>
      </ul>
      <div>
        <Trans parent="h3">Origin of data</Trans>
      </div>
      <Trans parent="p">
        MONEI may obtain such data: (i) directly provided by the customer when contacting MONEI,
        through their MONEI profile or collected during the contractual relationship, (ii) through
        public records (Commercial Registry, Tax Agency) or other information sources such as social
        media or internet, (iii) from third-party databases for anti-money laundering and terrorist
        financing prevention and fraud prevention, and (iv) data obtained from browsing.
      </Trans>
      <Trans parent="p">
        Identification data, data referring to your professional activity, as well as all contact
        data whose mandatory nature is expressly established and any others that may be required by
        applicable regulations are mandatory, and refusal to provide them will make it impossible to
        enter into the contract.
      </Trans>
      <div>
        <Trans parent="h3">Purposes of processing</Trans>
      </div>
      <Trans parent="p">The obtained data will be used and processed for:</Trans>
      <div>
        <Trans parent="h4">
          Based on the execution of the contractual relationship (Art.6.1.b GDPR):
        </Trans>
      </div>
      <ul>
        <Trans parent="li">
          Register you as a MONEI customer and maintain, develop and control the contractual
          relationship with the Business.
        </Trans>
        <Trans parent="li">Management of billing, payment disbursement and debit charges.</Trans>
      </ul>
      <div>
        <Trans parent="h4">Based on compliance with legal obligations (Art.6.1.c GDPR):</Trans>
      </div>
      <Trans parent="p">
        Comply with legal obligations derived from payment services regulations and anti-money
        laundering and terrorist financing regulations and any other applicable regulations.
      </Trans>
      <Trans parent="p">
        For these purposes and in accordance with the regulations and customer identification
        requirements imposed by Card Systems, MONEI will be obliged to carry out formal and real,
        professional or business identification of customers. In this sense, biometric verification
        of the identity of natural persons (Business representative or self-employed worker) is
        carried out through video-selfie or through qualified electronic signature.
      </Trans>
      <div>
        <Trans parent="h4">Based on MONEI's legitimate interest (Art.6.1.f GDPR):</Trans>
      </div>
      <ul>
        <Trans parent="li">
          Fraud prevention activities including communication of personal data to centralized
          information systems. This processing is based on MONEI's legitimate interest in preventing
          fraudulent activities.
        </Trans>
        <Trans parent="li">
          Sending commercial communications via email. The customer can oppose this processing at
          the time of data collection by checking the corresponding box, as well as by sending a
          communication to{' '}
          <AnchorLink href="mailto:support@monei.com">support@monei.com</AnchorLink> at any time.
          This processing is based on MONEI's legitimate interest in sending you information about
          MONEI's activity that may be of interest to you.
        </Trans>
        <Trans parent="li">
          Risk analysis. The merchant category will be evaluated using scoring assessment models,
          including through automated processing, taking into account, among other aspects, the
          business description and legal address. This processing is based on MONEI's legitimate
          interest in preventing breaches as well as economic and reputational damage. You can find
          more details about the logic applied in the{' '}
          <InternalPageLink to="/protection-policy-attachment">Annex I</InternalPageLink>
        </Trans>
      </ul>
      <Trans parent="p">
        If you want to obtain more information about the legitimate interest weighting tests carried
        out by MONEI, contact us through{' '}
        <AnchorLink href="mailto:support@monei.com">support@monei.com</AnchorLink>.
      </Trans>
      <div>
        <Trans parent="h3">Data retention</Trans>
      </div>
      <Trans parent="p">
        Personal data accessed will be processed while the contractual relationship is maintained
        and, subsequently, will be kept, duly blocked, during the limitation period for actions that
        may arise from said contractual relationship. In any case, MONEI will retain the information
        that the Anti-Money Laundering and Terrorist Financing regulations require to obtain for a
        period of 10 years from the termination of the business relationship or the execution of the
        operation.
      </Trans>
      <div>
        <Trans parent="h3">Data communication</Trans>
      </div>
      <Trans parent="p">MONEI may communicate personal data to:</Trans>
      <ul>
        <Trans parent="li">
          Competent regulatory and administrative authorities (including SEPBLAC, Tax Agency, State
          Security Forces and Bodies).
        </Trans>
        <Trans parent="li">
          In order to prevent fraudulent conduct, personal data may be sent to centralized
          information systems.
        </Trans>
        <Trans parent="li">
          Likewise, MONEI has third-party service providers who may access personal data due to the
          provision of their services, such as auditors, consulting services, advisors, IT
          maintenance, technology platforms, administrative services and document destruction, among
          others. MONEI pre-selects these providers based on data protection compliance criteria,
          has signed data processing agreements with all of them in accordance with Article 28.3 of
          the GDPR and controls that they comply with their obligations.
        </Trans>
      </ul>
      <div>
        <Trans parent="h3">International data transfers</Trans>
      </div>
      <Trans parent="p">
        As a general rule, no international transfers of personal data are made. In the event that
        any of MONEI's providers is located outside the European Economic Area ("EEA"), MONEI will
        enter into the corresponding contractual agreements to guarantee the implementation of
        adequate security measures to ensure that Users' personal data is processed with an adequate
        level of protection equivalent to that required in the EEA.
      </Trans>
      <div>
        <Trans parent="h2">Rights and complaints regarding data protection</Trans>
      </div>
      <Trans parent="p">
        Data subjects may exercise the following data protection rights by postal mail to Calle
        Palestina, 1, Entreplanta, 29007, Málaga, Spain or by email to{' '}
        <AnchorLink href="mailto:support@monei.com">support@monei.com</AnchorLink>, proving their
        identity:
      </Trans>
      <ul>
        <Trans parent="li">
          access: you can obtain information related to the processing of your personal data and a
          copy of such personal data.
        </Trans>
        <Trans parent="li">
          rectification: if you consider your personal data is inaccurate or incomplete, you can
          request that such data be modified accordingly.
        </Trans>
        <Trans parent="li">
          erasure: you can request the deletion of your personal data, to the extent permitted by
          Law.
        </Trans>
        <Trans parent="li">
          restriction: you can request the restriction of processing of your personal data.
        </Trans>
        <Trans parent="li">
          objection: you can object to the processing of your personal data, for reasons related to
          your particular situation.
        </Trans>
        <Trans parent="li">
          data portability: when legally applicable, you have the right to have the personal data
          you have provided to us returned or, when technically possible, transferred to a third
          party.
        </Trans>
        <Trans parent="li">
          not to be subject to automated individual decisions: aims to ensure that you are not
          subject to a decision based solely on the processing of your data, including profiling,
          which produces legal effects concerning you or similarly significantly affects you.
        </Trans>
      </ul>
      <Trans parent="p">
        Likewise, they may submit a complaint to MONEI's Data Protection Officer at the indicated
        addresses or to the control authority in the matter, Spanish Data Protection Agency (AEPD),
        whose contact details are offered through the website{' '}
        <AnchorLink href="https://www.aepd.es">aepd.es</AnchorLink>.
      </Trans>
    </Article>
  );
};

export default Page;

export const query = graphql`
  query ($language: String!) {
    locales: allLocale(
      filter: {ns: {in: ["common", "protection-policy"]}, language: {eq: $language}}
    ) {
      edges {
        node {
          ns
          data
          language
        }
      }
    }
  }
`;
